import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, CheckCheck, Loader2, Mic, ChevronDown, ChevronUp, AlertCircle, Video } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useTimezone } from '../../hooks/useTimezone';
import { toLocaleTimeStringInTz, toLocaleDateStringInTz } from '../../utils/dateFormatters';

interface ParsedMessage {
  timestamp: string;
  formattedDate: string;
  sender: 'user' | 'assistant';
  body: string;
  dateGroup: string;
  messageType?: string;
  audioUrl?: string | null;
  videoUrl?: string | null;
  deliveryStatus?: string | null;
}

interface InlineWhatsAppThreadProps {
  leadId: string;
  leadName?: string;
  companyName?: string;
}

function formatMsgTime(timestamp: string, tz?: string): string {
  try {
    const date = new Date(timestamp);
    return toLocaleTimeStringInTz(date, tz, 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

function getMsgDateGroup(timestamp: string, tz?: string): string {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';

    return toLocaleDateStringInTz(date, tz, 'en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return '';
  }
}

const InlineWhatsAppThread: React.FC<InlineWhatsAppThreadProps> = ({ leadId, leadName = 'Lead', companyName }) => {
  const tz = useTimezone();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<ParsedMessage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setLoading(true);

    supabase
      .from('whatsapp_logs')
      .select('from_role, message_text, message_type, status, created_at, audio_url, video_url')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data && data.length > 0) {
          setMessages(data.map((msg) => {
            const sender: 'user' | 'assistant' = msg.from_role === 'lead' ? 'user' : 'assistant';
            const ts = msg.created_at || '';
            return {
              timestamp: ts,
              formattedDate: formatMsgTime(ts, tz),
              sender,
              body: msg.message_text || '(empty)',
              dateGroup: getMsgDateGroup(ts, tz),
              messageType: msg.message_type || undefined,
              audioUrl: msg.audio_url || null,
              videoUrl: msg.video_url || null,
              deliveryStatus: msg.status || null,
            };
          }));
        } else {
          setMessages([]);
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [leadId]);

  useEffect(() => {
    if (scrollRef.current && !loading && messages.length > 0) {
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current!.scrollHeight });
      }, 50);
    }
  }, [loading, messages]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 size={24} className="mx-auto text-[#05E0D1] mb-3 animate-spin" />
        <p className="text-gray-400 text-sm font-medium">Loading messages...</p>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mx-auto mb-3">
          <MessageCircle size={20} className="text-green-400" />
        </div>
        <p className="text-sm text-gray-500 font-medium">No WhatsApp messages yet</p>
        <p className="text-xs text-gray-400 mt-1">WhatsApp conversations will appear here</p>
      </div>
    );
  }

  let lastDateGroup = '';

  return (
    <div>
      <div ref={scrollRef} className="max-h-[500px] overflow-y-auto custom-scrollbar">
        <div className="space-y-2.5">
          {messages.map((message, index) => {
            const showDateSeparator = message.dateGroup && message.dateGroup !== lastDateGroup;
            if (message.dateGroup) lastDateGroup = message.dateGroup;

            return (
              <React.Fragment key={index}>
                {showDateSeparator && <DateSeparator label={message.dateGroup} />}
                <WhatsAppBubble message={message} />
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="text-center pt-3 border-t border-gray-100 mt-3">
        <p className="text-xs text-gray-400 font-medium">
          {messages.length} message{messages.length !== 1 ? 's' : ''} in this conversation
        </p>
      </div>
    </div>
  );
};

const DateSeparator: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center justify-center py-3">
    <div className="flex items-center gap-3 w-full">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest font-sans px-2">
        {label}
      </span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  </div>
);

const WhatsAppBubble: React.FC<{ message: ParsedMessage }> = ({ message }) => {
  const isAgent = message.sender === 'assistant';
  const isAudio = message.messageType === 'audio' && message.audioUrl;
  const isVideo = message.messageType === 'video' && message.videoUrl;
  const [showTranscript, setShowTranscript] = useState(false);
  const [videoError, setVideoError] = useState(false);

  return (
    <div className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`relative max-w-[75%] px-3.5 py-2.5 ${
          isAgent
            ? 'bg-[#05E0D1]/15 rounded-lg rounded-tr-sm border border-[#05E0D1]/20'
            : 'bg-gray-200 rounded-lg rounded-tl-sm'
        }`}
      >
        {isVideo ? (
          <div className="min-w-[260px]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-md flex items-center justify-center">
                <Video className="text-white" size={10} />
              </div>
              <span className="text-[11px] font-semibold text-gray-600">Video Message</span>
            </div>
            {videoError ? (
              <div className="w-full rounded-md bg-gray-100 border border-gray-200 flex flex-col items-center justify-center py-6 px-4">
                <AlertCircle size={24} className="text-gray-400 mb-2" />
                <span className="text-xs text-gray-500 font-medium">Video unavailable</span>
              </div>
            ) : (
              <video
                controls
                className="w-full rounded-md"
                preload="metadata"
                style={{ maxHeight: '240px' }}
                onError={() => setVideoError(true)}
              >
                <source src={message.videoUrl!} type="video/mp4" />
              </video>
            )}
            {message.body && message.body !== '(empty)' && (
              <div className="mt-2">
                <button
                  onClick={() => setShowTranscript(!showTranscript)}
                  className="flex items-center gap-1 text-[11px] font-medium text-[#1875FF] hover:text-[#1260CC] transition-colors"
                >
                  {showTranscript ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  Video transcript
                </button>
                {showTranscript && (
                  <p className="mt-1.5 text-[12px] leading-relaxed text-gray-500 border-t border-gray-200/60 pt-1.5 whitespace-pre-wrap">
                    {message.body}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : isAudio ? (
          <div className="min-w-[220px]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-md flex items-center justify-center">
                <Mic className="text-white" size={10} />
              </div>
              <span className="text-[11px] font-semibold text-gray-600">Voice Note</span>
            </div>
            <audio controls className="w-full h-8" preload="metadata" style={{ minWidth: '200px' }}>
              <source src={message.audioUrl!} type="audio/ogg" />
              <source src={message.audioUrl!} type="audio/mpeg" />
              <source src={message.audioUrl!} type="audio/mp4" />
            </audio>
            {message.body && message.body !== '(empty)' && (
              <div className="mt-2">
                <button
                  onClick={() => setShowTranscript(!showTranscript)}
                  className="flex items-center gap-1 text-[11px] font-medium text-[#1875FF] hover:text-[#1260CC] transition-colors"
                >
                  {showTranscript ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  Voice note transcript
                </button>
                {showTranscript && (
                  <p className="mt-1.5 text-[12px] leading-relaxed text-gray-500 border-t border-gray-200/60 pt-1.5 whitespace-pre-wrap">
                    {message.body}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className={`text-[13.5px] leading-relaxed whitespace-pre-wrap break-words pr-16 ${
            isAgent ? 'text-gray-800' : 'text-gray-700'
          }`}>
            {message.body}
          </p>
        )}

        {isAgent && message.deliveryStatus === 'failed' ? (
          <div className="flex items-center gap-1 justify-end mt-1">
            <span className="text-[10.5px] text-red-400">Not delivered</span>
            <span className="text-[10.5px] text-red-300">|</span>
            <span className="text-[10.5px] text-red-400">{message.formattedDate}</span>
            <AlertCircle size={12} className="text-red-400" />
          </div>
        ) : (
          <div className={`flex items-center gap-1 justify-end ${isAudio || isVideo ? 'mt-1' : '-mt-3'} float-right ml-3 relative top-1`}>
            {message.formattedDate && (
              <span className={`text-[10.5px] ${isAgent ? 'text-[#05E0D1]/80' : 'text-gray-400'}`}>
                {message.formattedDate}
              </span>
            )}
            {isAgent && <CheckCheck size={14} className="text-[#05E0D1]" />}
          </div>
        )}
      </div>
    </div>
  );
};

export default InlineWhatsAppThread;
